import * as React from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import Link from '@mui/joy/Link';
import Sheet from '@mui/joy/Sheet';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import DebounceInput from '../../../../components/debounceInput/DebounceInput';
import { BotRow } from './aggregate';

type SortKey = 'botId' | 'total' | 'sharePercent' | 'successes' | 'failures' | 'successRate';

type Props = {
    rows: BotRow[];
    selectedIds: number[];
    colors: Map<number, string>;
    onToggle: (botId: number) => void;
    onSetSelected: (ids: number[]) => void;
};

const COLUMNS: Array<{ key: SortKey; label: string; width?: number; align?: 'right' }> = [
    { key: 'botId', label: 'Бот', width: 220 },
    { key: 'total', label: 'Обмены', align: 'right' },
    { key: 'sharePercent', label: 'Доля', align: 'right' },
    { key: 'successes', label: 'Успехи', align: 'right' },
    { key: 'failures', label: 'Ошибки', align: 'right' },
    { key: 'successRate', label: 'Success %', align: 'right' },
];

function compareRows(a: BotRow, b: BotRow, key: SortKey, dir: 'asc' | 'desc'): number {
    const mul = dir === 'asc' ? 1 : -1;
    const av = a[key];
    const bv = b[key];
    if (av === bv) {
        return a.botId - b.botId;
    }
    return (av < bv ? -1 : 1) * mul;
}

export function BotsStatsTable(props: Props) {
    const { rows, selectedIds, colors, onToggle, onSetSelected } = props;
    const [query, setQuery] = React.useState('');
    const [sortKey, setSortKey] = React.useState<SortKey>('total');
    const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');

    const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q
            ? rows.filter(
                  (r) =>
                      r.label.toLowerCase().includes(q) ||
                      String(r.botId).includes(q),
              )
            : rows.slice();
        list.sort((a, b) => compareRows(a, b, sortKey, sortDir));
        return list;
    }, [rows, query, sortKey, sortDir]);

    const allFilteredSelected =
        filtered.length > 0 && filtered.every((r) => selectedSet.has(r.botId));

    const toggleAllFiltered = () => {
        if (allFilteredSelected) {
            const filteredSet = new Set(filtered.map((r) => r.botId));
            onSetSelected(selectedIds.filter((id) => !filteredSet.has(id)));
            return;
        }
        const next = new Set(selectedIds);
        for (const row of filtered) {
            next.add(row.botId);
        }
        onSetSelected(Array.from(next));
    };

    const onSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(key);
        setSortDir(key === 'botId' ? 'asc' : 'desc');
    };

    const sortIcon = (key: SortKey) => {
        if (sortKey !== key) {
            return null;
        }
        return sortDir === 'asc'
            ? <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} />
            : <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} />;
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 1,
                }}
            >
                <Box>
                    <Typography level="title-sm">Все боты</Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        {rows.length} шт. Чекбокс — на график тренда
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        disabled={filtered.length === 0}
                        onClick={toggleAllFiltered}
                    >
                        {allFilteredSelected ? 'Снять все' : 'Выбрать все'}
                    </Button>
                    <DebounceInput
                        size="sm"
                        placeholder="Поиск по id / нику"
                        startDecorator={<SearchIcon />}
                        debounceTimeout={200}
                        sx={{ minWidth: 220 }}
                        handleDebounce={(val) => setQuery(val)}
                    />
                </Box>
            </Box>

            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1 }}>
                {filtered.length === 0 ? (
                    <Typography level="body-sm" sx={{ color: 'neutral.500', py: 2 }}>
                        Ничего не найдено
                    </Typography>
                ) : (
                    filtered.map((row) => {
                    const checked = selectedSet.has(row.botId);
                    return (
                        <Sheet key={row.botId} variant="outlined" sx={{ p: 1.5, borderRadius: 'sm' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Checkbox
                                    size="sm"
                                    checked={checked}
                                    onChange={() => onToggle(row.botId)}
                                    sx={{ mt: 0.25 }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: colors.get(row.botId) ?? 'neutral.400',
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Link
                                            level="body-sm"
                                            href={`/app/bots/${row.botId}#stats`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {row.label}
                                        </Link>
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                                        {row.total.toLocaleString('ru-RU')} обменов · {row.sharePercent.toFixed(1)}% ·
                                        success {row.successRate.toFixed(1)}% · ошибок {row.failures.toLocaleString('ru-RU')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Sheet>
                    );
                })
                )}
            </Box>

            <Sheet
                variant="outlined"
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    width: '100%',
                    borderRadius: 'sm',
                    overflow: 'auto',
                    maxHeight: 480,
                }}
            >
                <Table
                    stickyHeader
                    hoverRow
                    size="sm"
                    borderAxis="xBetween"
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                        '--TableCell-cornerRadius': '0px',
                        borderRadius: 0,
                        '& thead th:first-of-type, & thead th:last-of-type': {
                            borderRadius: 0,
                        },
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 36 }} />
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    style={{
                                        width: col.width,
                                        textAlign: col.align,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                    }}
                                    onClick={() => onSort(col.key)}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start',
                                            width: '100%',
                                        }}
                                    >
                                        {col.label}
                                        {sortIcon(col.key)}
                                    </Box>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={7}>
                                    <Typography level="body-sm" sx={{ color: 'neutral.500', py: 2, textAlign: 'center' }}>
                                        Ничего не найдено
                                    </Typography>
                                </td>
                            </tr>
                        ) : (
                            filtered.map((row) => {
                            const checked = selectedSet.has(row.botId);
                            return (
                                <tr key={row.botId}>
                                    <td>
                                        <Checkbox
                                            size="sm"
                                            checked={checked}
                                            onChange={() => onToggle(row.botId)}
                                        />
                                    </td>
                                    <td>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: colors.get(row.botId) ?? 'neutral.400',
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Link
                                                level="body-sm"
                                                href={`/app/bots/${row.botId}#stats`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {row.label}
                                            </Link>
                                        </Box>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{row.total.toLocaleString('ru-RU')}</td>
                                    <td style={{ textAlign: 'right' }}>{row.sharePercent.toFixed(1)}%</td>
                                    <td style={{ textAlign: 'right' }}>{row.successes.toLocaleString('ru-RU')}</td>
                                    <td style={{ textAlign: 'right' }}>{row.failures.toLocaleString('ru-RU')}</td>
                                    <td style={{ textAlign: 'right' }}>{row.successRate.toFixed(1)}%</td>
                                </tr>
                            );
                            })
                        )}
                    </tbody>
                </Table>
            </Sheet>
        </Box>
    );
}
